// itemIndex: types for /api/item-index and /api/item-index/equipment, plus the
// pure search/filter helpers of the Items tab. Ported from mogugi.
export interface IndexMetalware { id: number; level: number }
export interface IndexEnchantEffect { code: number; value: number; condSkill?: number; condRank?: number }
interface ItemExtra {
    enchantPrefix?: number; enchantSuffix?: number;
    durability?: number; durabilityMax?: number; defense?: number; protection?: number;
    attackMin?: number; attackMax?: number;
    injuryMin?: number; injuryMax?: number; balance?: number; critical?: number;
    metalware?: IndexMetalware[];
    prefixEffects?: IndexEnchantEffect[];
    suffixEffects?: IndexEnchantEffect[];
    blessEffects?: IndexEnchantEffect[];
    relicEffects?: IndexEnchantEffect[];
    bagItemId?: number;  // item id of the bag this item sits in (0/undefined = not in a bag)
    bagName?: string;    // display name for named containers such as bank tabs
    pocket?: number;     // raw pocket id
    colors?: string[];   // six rrggbb hex dye colors
    metadata?: string;   // raw MetaData1 KV string
}
export interface IndexItem extends ItemExtra {
    id: number; qty: number; storage: string; container: string; x: number; y: number;
}
export interface IndexEntity { entity: string; master: string; account?: string; items: IndexItem[] }
export interface Holder extends ItemExtra {
    id: number; entity: string; master: string; qty: number; storage: string; container: string; x: number; y: number;
}

export interface EquipmentItem { pocket: number; eid: string; item: IndexItem }
export interface OwnerEquipment { id: string; name: string; items: EquipmentItem[] }

// parseItemMetadata parses a MetaData1 KV string ("KEY:type:value;...") into a map.
export function parseItemMetadata(s?: string): Record<string, string> {
    const out: Record<string, string> = {};
    if (!s) return out;
    for (const tok of s.split(';')) {
        if (!tok) continue;
        const parts = tok.split(':');
        if (parts.length < 3) continue;
        out[parts[0]] = parts.slice(2).join(':');
    }
    return out;
}

// flattenIndex turns the per-entity /api/item-index payload into one row per held item.
export function flattenIndex(data: IndexEntity[]): Holder[] {
    const out: Holder[] = [];
    for (const ent of data) {
        for (const it of ent.items) out.push({ ...it, entity: ent.entity, master: ent.master });
    }
    return out;
}

export type SearchQuery =
    | { kind: 'empty' }
    | { kind: 'id'; id: number }
    | { kind: 'text'; needle: string }
    | { kind: 'regex'; re: RegExp }
    | { kind: 'error'; message: string };

// Allowed regex flags. `g` is excluded on purpose: it makes RegExp.test()
// stateful via lastIndex, which would drop rows at random while filtering.
const ALLOWED_FLAGS = 'imsu';

// parseSearchQuery classifies the search box input: `/pattern/flags` is a
// regex, a bare number matches an item id, anything else is a lowercase
// substring. `i` is always forced on because the text matched against is
// pre-lowercased.
export function parseSearchQuery(raw: string): SearchQuery {
    const q = (raw ?? '').trim();
    if (!q) return { kind: 'empty' };

    const m = /^\/(.+)\/([a-z]*)$/.exec(q);
    if (m) {
        const flags = new Set([...m[2]].filter(f => ALLOWED_FLAGS.includes(f)));
        flags.add('i');
        try {
            return { kind: 'regex', re: new RegExp(m[1], [...flags].join('')) };
        } catch (e) {
            return { kind: 'error', message: `Invalid regex: ${(e as Error).message}` };
        }
    }
    if (/^\d+$/.test(q)) return { kind: 'id', id: Number(q) };
    return { kind: 'text', needle: q.toLowerCase() };
}

export type ExcludeColumn = 'item' | 'entity' | 'storage';
export interface ExcludeEntry { col: ExcludeColumn; value: string }
export interface ExcludeSets { item: Set<string>; entity: Set<string>; storage: Set<string> }

const EXCLUDE_COLUMNS: ExcludeColumn[] = ['item', 'entity', 'storage'];

// parseExcludeEntries validates a localStorage payload entry by entry, so one
// bad record cannot take the whole tab down.
export function parseExcludeEntries(raw: string | null): ExcludeEntry[] {
    if (!raw) return [];
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return [];
    }
    if (!Array.isArray(parsed)) return [];

    const out: ExcludeEntry[] = [];
    const seen = new Set<string>();
    for (const e of parsed) {
        if (!e || typeof e !== 'object') continue;
        const { col, value } = e as { col?: unknown; value?: unknown };
        if (typeof value !== 'string' || !value) continue;
        if (!EXCLUDE_COLUMNS.includes(col as ExcludeColumn)) continue;
        const key = `${col as string}:${value}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ col: col as ExcludeColumn, value });
    }
    return out;
}

// buildExcludeSets turns the list into per-column lookups, so filtering stays
// O(1) per row no matter how long the list grows.
export function buildExcludeSets(entries: ExcludeEntry[]): ExcludeSets {
    const sets: ExcludeSets = { item: new Set(), entity: new Set(), storage: new Set() };
    for (const e of entries) sets[e.col].add(e.value);
    return sets;
}

export function isExcludeEmpty(sets: ExcludeSets): boolean {
    return sets.item.size === 0 && sets.entity.size === 0 && sets.storage.size === 0;
}
